import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import isNumber from 'lodash/isNumber';
import isFunction from 'lodash/isFunction';
import { Controller } from 'react-hook-form';

import { getInputError } from 'src/components/UI/inputs/common';
import { FormPropsType } from 'src/components/UI/types';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { useHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { uiMemoFormPropsInputCompareFn } from 'src/components/UI/UIMemoFormPropsCompareFn';
import { inputBaseHook } from 'src/utils/hooks/inputs';
import { createArrayXLength } from 'src/utils/objects';
import { InputMaterialWrapper } from 'src/components/UI/inputs/common/InputMaterialWrapper';

type Props = {
  formProps: FormPropsType;
  name: string;

  max?: number; // value. not a length because a value is more flexible
  min?: number;
  allowNegative?: boolean;
  thousandSeparator?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  prefix?: string;
  postfix?: string;
  required?: boolean;
  autoFocus?: boolean;
  decimalScale?: number;
  onlyInteger?: boolean; // synonym for decimalScale={0}
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  wrapperClassName?: string;
  id?: string;
  zeroInsteadEmptyString?: boolean;
  allowZeroValue?: boolean;
  errorLabel?: string;
  showHoverError?: boolean;
  material?: boolean; // new field-material design
  defaultPlaceholderStyle?: boolean;
  validationName?: string;
};

export const UiFormatNumberInput: React.FC<Props> = memo(
  ({
    formProps: {
      clearErrors,
      control,
      setError,
      getValues,
      setValue,
      formState: { errors },
    },
    name,
    max = 999999,
    min = 0,
    allowNegative = false,
    thousandSeparator = ',',
    placeholder,
    disabled,
    className,
    prefix,
    postfix,
    required = false,
    autoFocus = false,
    decimalScale = 2,
    onlyInteger,
    onBlur,
    wrapperClassName,
    id,
    zeroInsteadEmptyString,
    allowZeroValue,
    errorLabel,
    showHoverError,
    material,
    defaultPlaceholderStyle = false,
    validationName,
  }: Props) => {
    const { t } = useTranslation();

    const [active, setActive] = useState(false);
    const [hasValue, setHasValue] = useState(getValues(name) !== null);

    const mountRef = useRef(false);

    useEffect(() => inputBaseHook(mountRef, name, clearErrors), [clearErrors, name]);

    const { hoverMessage, handleChangeInputHover } = useHoverErrors({
      name,
      clearErrors,
      error: getInputError(errors, name, validationName),
      showAnyError: showHoverError || material,
      validationName,
    });

    /** make it have 2 decimal trailing zeros */
    const formatValue = useCallback((value: string | null): string | null => {
      let localValue = value ? value.toString() : null;

      if (localValue && /\.\d+/.test(localValue) && !/\.\d+\d+/.test(localValue)) {
        const [ints, decimals] = localValue.split('.');

        localValue = `${ints}.${decimals}0`;
      }

      return localValue ?? null;
    }, []);

    const preparePlaceholder: string = useMemo(() => {
      if (placeholder) {
        return placeholder;
      }

      if (!onlyInteger && decimalScale > 0) {
        return `0.${createArrayXLength(decimalScale)
          .map(() => 0)
          .join('')}`;
      }
      return '0';
    }, [decimalScale, onlyInteger, placeholder]);

    const Component = (
      <Controller
        control={control}
        name={name}
        defaultValue={getValues(name)}
        rules={{
          validate: (v) => {
            if (mountRef.current) {
              if (required && v !== 0 && !v) {
                return t('common.validation.required', { label: errorLabel || name }).toString();
              }
            }

            return undefined;
          },
        }}
        render={({ field }) => {
          const error = getInputError(errors, name, validationName);

          // remove onChange (onValueChange instead of onChange)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { onChange, ...restField } = field;

          const value = formatValue(field.value);

          setHasValue(value !== null);

          return (
            <UiHoverTooltip id={name} text={hoverMessage} render={!!hoverMessage && !material} noWrapper>
              <NumberFormat
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...restField}
                data-name={name}
                thousandSeparator={thousandSeparator}
                allowNegative={isNumber(min) && min < 0 ? true : allowNegative}
                autoFocus={autoFocus}
                decimalScale={onlyInteger ? 0 : decimalScale}
                id={id}
                // fixedDecimalScale={!(onlyInteger || decimalScale === 0)} // it brakes behavior
                isAllowed={({ floatValue, value }) => {
                  const allowedErrors = [];

                  // for values similar to 000000
                  if (floatValue === 0 && value.split('.')[0].length > 1) {
                    allowedErrors.push('too many zeros');
                  } else if (floatValue === 0 && !allowZeroValue && !zeroInsteadEmptyString) {
                    allowedErrors.push('zero value');
                  }
                  if (max && floatValue && floatValue > max) {
                    allowedErrors.push('max');
                  }
                  return !allowedErrors.length;
                }}
                value={value}
                disabled={disabled}
                placeholder={material && placeholder && !defaultPlaceholderStyle ? undefined : preparePlaceholder}
                className={classNames(className, `${prefix ? `mp-${prefix.length}` : ''}`, {
                  required: !!error && required,
                  error: !!error,
                  disabled,
                })}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (!(onlyInteger || decimalScale === 0)) {
                    // https://github.com/s-yadav/react-number-format/issues/115#issuecomment-342141253
                    const { key, target } = e;

                    const {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      selectionStart,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      value,
                    } = target;

                    /** replace ',' to '.' when enter decimals. allow both ',' and '.' */
                    if (key === ',' && !value.endsWith('.')) {
                      e.preventDefault();
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      target.value = `${value.substring(0, selectionStart)}.${value.substring(
                        selectionStart,
                        value.length
                      )}`;
                    }
                  }
                }}
                onValueChange={({ floatValue }) => {
                  field.onChange(floatValue ?? null);

                  handleChangeInputHover();

                  /** this way seems to be better than preventing input */
                  if (min && floatValue && floatValue < min) {
                    setError(name, {
                      type: 'manual',
                      message: t(`common.validation.numberMin`, { name, value: floatValue, min }),
                    });
                  }
                }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur();

                  if (!disabled) {
                    setActive(false);
                  }

                  if (isFunction(onBlur)) {
                    onBlur(event);
                  }
                  if (zeroInsteadEmptyString && !field.value && field.value !== 0) {
                    setValue(name, 0);
                  }
                }}
                onFocus={() => {
                  if (!disabled) {
                    setActive(true);
                  }
                }}
              />
            </UiHoverTooltip>
          );
        }}
      />
    );

    if (material) {
      return (
        <InputMaterialWrapper
          active={active || !!prefix || !!postfix}
          disabled={disabled}
          hasErrors={!!hoverMessage}
          errorMessage={hoverMessage}
          placeholder={defaultPlaceholderStyle ? undefined : placeholder}
          hasValue={hasValue}
          className={wrapperClassName}
        >
          {prefix && <span className="prefix">{prefix}</span>}
          {Component}
          {postfix && <span className="postfix">{postfix}</span>}
        </InputMaterialWrapper>
      );
    }

    return prefix || postfix ? (
      <div className={classNames('input-group', wrapperClassName)}>
        {prefix && <span className="prefix">{prefix}</span>}
        {Component}
        {postfix && <span className="postfix">{postfix}</span>}
      </div>
    ) : (
      Component
    );
  },
  uiMemoFormPropsInputCompareFn
);
